/**
 * A commercial's login, on their sheet: created once, here, rather than a second time in
 * Administration.
 *
 * Without one, an address opens it — the person receives the invitation and chooses a password
 * — or an account that already holds the Commercial profile is linked. With one, the invitation
 * can go out again while it is unused, and access can be taken away: the login loses the
 * Commercial profile and sees no customer any more, but the account itself stays for
 * Administration to suspend or close.
 */

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { Badge, Button, Input, Select, useToast } from "@hardauto/core-ui";

import type { ClientsApi, InvitationOutcome, SalesRep } from "../../api";
import { NAMESPACE } from "../../i18n";

const STATUS_TONE = { invited: "warning", active: "success", disabled: "neutral" } as const;

export function RepAccessSection({
  api,
  rep,
  mayGiveLogin,
  onChanged,
}: {
  api: ClientsApi;
  rep: SalesRep;
  mayGiveLogin: boolean;
  onChanged: (rep: SalesRep) => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();
  const [email, setEmail] = useState("");
  const [existing, setExisting] = useState("");

  const unlinkedFetcher = useCallback(
    () =>
      mayGiveLogin && !rep.account
        ? api.unlinkedSalesRepAccounts()
        : Promise.resolve({ items: [] }),
    [api, mayGiveLogin, rep.account],
  );
  const unlinked = useResource(unlinkedFetcher);

  /** What the invitation did, worded; nothing when there was nothing to say. */
  function announce(outcome: InvitationOutcome | null | undefined) {
    if (!outcome) return;
    const tone = outcome === "sent" || outcome === "not_needed" ? "success" : "warning";
    toast.show(t(`salesReps.access.invitation.${outcome}`, { defaultValue: outcome }), { tone });
  }

  function refused(error: unknown) {
    const reason =
      error instanceof ApiError && error.status === 409
        ? typeof error.context.reason === "string"
          ? error.context.reason
          : ""
        : "";
    toast.show(
      reason
        ? t(`salesReps.access.refused.${reason}`, { defaultValue: t("error.save") })
        : error instanceof ApiError && error.status === 422
          ? t("salesReps.access.badEmail")
          : t("error.save"),
      { tone: "danger" },
    );
  }

  function give(address: string) {
    void action.run("give", async () => {
      try {
        const updated = await api.giveSalesRepAccount(rep.code, address);
        announce(updated.invitation);
        setEmail("");
        setExisting("");
        onChanged(updated);
      } catch (error) {
        refused(error);
      }
    });
  }

  function resend() {
    void action.run("resend", async () => {
      try {
        announce((await api.resendSalesRepInvitation(rep.code)).invitation);
      } catch {
        toast.show(t("error.save"), { tone: "danger" });
      }
    });
  }

  function take() {
    void action.run("take", async () => {
      try {
        onChanged(await api.takeSalesRepAccount(rep.code));
        toast.show(t("salesReps.access.taken"), { tone: "success" });
      } catch {
        toast.show(t("error.save"), { tone: "danger" });
      }
    });
  }

  return (
    <section className="hardauto-rep-section" aria-label={t("salesReps.access.title")}>
      <h3>{t("salesReps.access.title")}</h3>
      {rep.account ? (
        <>
          <p>
            {rep.account.email}{" "}
            <Badge tone={STATUS_TONE[rep.account.status]}>
              {t(`salesReps.access.status.${rep.account.status}`)}
            </Badge>
          </p>
          {mayGiveLogin ? (
            <div className="hardauto-inline-form__actions">
              {rep.account.status === "invited" ? (
                <Button
                  size="sm"
                  onClick={resend}
                  busy={action.isBusy("resend")}
                  busyLabel={t("salesReps.access.resend")}
                >
                  {t("salesReps.access.resend")}
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="danger"
                onClick={take}
                busy={action.isBusy("take")}
                busyLabel={t("salesReps.access.take")}
              >
                {t("salesReps.access.take")}
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <>
          <p className="hardauto-cell__muted">{t("salesReps.access.none")}</p>
          {mayGiveLogin ? (
            <>
              <form
                className="hardauto-inline-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (email.trim()) give(email.trim());
                }}
              >
                <div className="hardauto-inline-form__field">
                  <Field label={t("salesReps.access.email")} hint={t("salesReps.access.emailHint")}>
                    {(props) => (
                      <Input
                        {...props}
                        type="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                      />
                    )}
                  </Field>
                </div>
                <div className="hardauto-inline-form__actions">
                  <Button
                    type="submit"
                    size="sm"
                    variant="primary"
                    busy={action.isBusy("give")}
                    busyLabel={t("salesReps.access.give")}
                  >
                    {t("salesReps.access.give")}
                  </Button>
                </div>
              </form>
              {(unlinked.data?.items ?? []).length > 0 ? (
                <div className="hardauto-inline-form">
                  <div className="hardauto-inline-form__field">
                    <Field label={t("salesReps.access.link")}>
                      {(props) => (
                        <Select
                          {...props}
                          value={existing}
                          onChange={(event) => setExisting(event.target.value)}
                        >
                          <option value="">—</option>
                          {(unlinked.data?.items ?? []).map((account) => (
                            <option key={account.user_id} value={account.email}>
                              {account.display_name
                                ? `${account.display_name} (${account.email})`
                                : account.email}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>
                  </div>
                  <div className="hardauto-inline-form__actions">
                    <Button size="sm" disabled={!existing} onClick={() => give(existing)}>
                      {t("salesReps.access.linkSubmit")}
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </section>
  );
}
